import { useId } from 'react';
import { GENERATED_PASSWORD_LENGTH, generatePassword } from './passwordGenerator.ts';

interface PasswordEntryProps {
  readonly id: string;
  readonly label: string;
  readonly value: string;
  readonly disabled: boolean;
  readonly onChange: (value: string) => void;
}

/**
 * A new-password box with a Generate button beside it.
 *
 * Nothing is validated here, for the reason `Users.tsx` gives: the length rule
 * lives on the server, which answers with the sentence that states it, and a
 * copy of the number in the console would be free to drift from the one that
 * decides. The box stays masked even after Generate — the value is shown once,
 * deliberately, in the panel that appears after it has been set.
 */
export const PasswordEntry = ({ id, label, value, disabled, onChange }: PasswordEntryProps) => {
  const hintId = useId();
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <div className="pf-password-row">
        <input
          id={id}
          type="password"
          autoComplete="new-password"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          value={value}
          disabled={disabled}
          aria-describedby={hintId}
          onChange={(event) => onChange(event.target.value)}
        />
        <button
          type="button"
          className="btn btn-sm"
          disabled={disabled}
          onClick={() => onChange(generatePassword())}
        >
          Generate
        </button>
      </div>
      <span className="hint" id={hintId}>
        Type one, or generate {GENERATED_PASSWORD_LENGTH} random characters. Either way it is shown
        to you once, after it has been set.
      </span>
    </div>
  );
};
